import {
  IQuoteWithBillGroup,
  QuoteStatusEnum,
  QuoteTypeEnum,
  QuoteTypeEnumDisplay,
} from '@monetize/types/app';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { actionCatalogColumn } from '../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  currencyBodyTemplate,
  dateTimeBodyTemplate,
  durationInMonthsTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  nameWithIdBodyTemplate,
  userAvatarBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import { ContractLookupPopover } from '../../../components/Monetize/LookupPopover/ContractLookupPopover';
import {
  QuoteStatusEnumDisplay,
  QuoteStatusTagStyle,
} from '../../../constants/quotes';
import {
  getBillGroupPageRoute,
  getQuoteBaseRoute,
} from '../../../constants/routes';

const columnHelper = createColumnHelper<IQuoteWithBillGroup>();

export type QuoteListColumnContext = ColumnContextBase<IQuoteWithBillGroup>;

/** tableId: FilterStateKeys.QUOTE_LIST */
export const QUOTE_LIST_CATALOG: ColumnCatalog<
  IQuoteWithBillGroup,
  QuoteListColumnContext
> = [
  {
    id: 'description',
    label: 'Name',
    customizable: false,
    build: () =>
      columnHelper.accessor('description', {
        ...getColHeaderWithSize('Name', 280),
        ...getColHeaderFilters('TEXT'),
        enableSorting: true,
        cell: nameWithIdBodyTemplate<IQuoteWithBillGroup, string>({
          nameProp: 'description',
          idProp: 'id',
          idLinkFn: (id) => getQuoteBaseRoute(id),
        }),
      }),
  },
  {
    id: 'ownerName',
    label: 'Owner',
    build: () =>
      columnHelper.accessor('ownerName', {
        ...getColHeaderWithSize('Owner', 95),
        ...getColHeaderFilters('OWNER', { filterKey: 'owner' }),
        enableSorting: false,
        cell: userAvatarBodyTemplate(),
      }),
  },
  {
    id: 'type',
    label: 'Type',
    build: () =>
      columnHelper.accessor('type', {
        ...getColHeaderWithSize('Type', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            QuoteTypeEnum,
            QuoteTypeEnumDisplay,
          ),
        }),
        cell: enumDisplayBodyTemplate({ displayMap: QuoteTypeEnumDisplay }),
        enableSorting: true,
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            QuoteStatusEnum,
            QuoteStatusEnumDisplay,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: QuoteStatusEnumDisplay,
          styleMap: QuoteStatusTagStyle,
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'amount',
    label: 'Total',
    build: () =>
      columnHelper.accessor('amount', {
        ...getColHeaderWithSize('Total', 150),
        ...getColHeaderFilters('NUMBER'),
        cell: currencyBodyTemplate({ currencyProperty: 'currency' }),
        enableSorting: true,
      }),
  },
  {
    id: 'contractLength',
    label: 'Length',
    build: () =>
      columnHelper.accessor('contractLength', {
        ...getColHeaderWithSize('Length', 115),
        cell: durationInMonthsTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'contractStartDate',
    label: 'Start Date',
    build: () =>
      columnHelper.accessor('contractStartDate', {
        ...getColHeaderWithSize('Start Date', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'contractEndDate',
    label: 'End Date',
    build: () =>
      columnHelper.accessor('contractEndDate', {
        ...getColHeaderWithSize('End Date', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    // Explicit id (no dots): TanStack derives accessorKey ids by replacing `.`
    // with `_`, so this keeps the catalog id and the real column id in sync.
    id: 'billGroup_id',
    label: 'Bill Group',
    build: () =>
      columnHelper.accessor('billGroup.id', {
        id: 'billGroup_id',
        ...getColHeaderWithSize('Bill Group', 280),
        enableSorting: false,
        cell: nameWithIdBodyTemplate<IQuoteWithBillGroup, string>({
          nameProp: 'billGroup.name',
          idProp: 'billGroup.id',
          idLinkFn: (id, record) => getBillGroupPageRoute(record.billGroupId!),
        }),
      }),
  },
  {
    id: 'contractId',
    label: 'Contract',
    build: () =>
      columnHelper.accessor('contractId', {
        ...getColHeaderWithSize('Contract', 200),
        enableSorting: true,
        cell: ({ row }) =>
          row.original.contractId ? (
            <ContractLookupPopover
              displayLabel={`${row.original.contractStartDate} - ${row.original.contractEndDate}`}
              value={row.original.contractId}
            />
          ) : null,
      }),
  },

  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 180),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        ...getColHeaderWithSize('Created', 180),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  actionCatalogColumn<IQuoteWithBillGroup>(),
];
